import { Heart, Instagram as InstagramIcon } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { useSite } from "@/lib/SiteContext";
import { INSTAGRAM_IMAGES } from "@/data/siteData";
import { Em, Reveal } from "./primitives";

export default function Instagram({ content }) {
  const { brand, contact } = useSite();
  const c = content || {};
  const images = c.images?.length ? c.images : INSTAGRAM_IMAGES.map((i) => i.src);
  const igUrl = contact.instagramUrl || contact.instagram_url;
  const handle = contact.instagram;

  return (
    <section id="instagram" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-5xl px-6 md:px-12">
        <Reveal className="mb-14 text-center md:mb-16">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-rose md:text-sm">
            {c.kicker || "06 — Instagram"}
          </p>
          <h2 className="font-serif text-4xl font-medium tracking-tight text-ink md:text-6xl">
            <Em text={c.title || "Follow Our *Gifting Journey*"} />
          </h2>
        </Reveal>

        <Reveal>
          <div
            data-testid="instagram-profile"
            className="overflow-hidden rounded-3xl border border-line bg-cream/40"
          >
            <div className="flex flex-col items-center gap-6 border-b border-line/70 px-6 py-10 sm:flex-row sm:items-center sm:gap-8 sm:px-10">
              <span className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-rose/40 bg-white shadow-sm md:h-24 md:w-24">
                {brand.logo ? (
                  <img src={imgUrl(brand.logo)} alt={brand.name} className="h-full w-full object-cover" />
                ) : (
                  <Heart size={30} className="fill-rose text-rose" />
                )}
              </span>
              <div className="flex-1 text-center sm:text-left">
                <p className="font-serif text-2xl font-semibold text-ink md:text-3xl">{brand.name}</p>
                <p data-testid="instagram-handle" className="mt-1 text-sm font-medium text-rose">@{handle}</p>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[#5C5C5C] sm:mx-0">
                  {c.blurb || "Behind-the-scenes wrapping, new hampers and the smiles they create — daily on Instagram."}
                </p>
              </div>
              <a
                data-testid="instagram-follow-button"
                href={igUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-2.5 rounded-full bg-rose px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-rose-dark"
              >
                <InstagramIcon size={17} /> Follow @{handle}
              </a>
            </div>

            <div data-testid="instagram-grid" className="grid grid-cols-3 gap-1 p-1 md:gap-2 md:p-2">
              {images.map((src, i) => (
                <a
                  key={src + i}
                  data-testid={`instagram-tile-${i + 1}`}
                  href={igUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block overflow-hidden rounded-xl md:rounded-2xl"
                  aria-label={`Instagram post ${i + 1}`}
                >
                  <img
                    src={imgUrl(src)}
                    alt={`Instagram post ${i + 1}`}
                    loading="lazy"
                    className="aspect-square w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-ink/0 opacity-0 transition-all duration-500 group-hover:bg-ink/40 group-hover:opacity-100">
                    <InstagramIcon size={26} className="text-white" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
